/**
 * Configuration interface and utilities for the polling service
 */

export interface PollingConfiguration {
  userHazardsInterval: number; // in milliseconds
  communityHazardsInterval: number; // in milliseconds
  enabled: boolean;
  maxRetries: number;
  retryBackoffMs: number;
  enabledOnMobile: boolean; // Allow disabling on mobile to save battery
  enabledOnMeteredConnection: boolean; // Allow disabling on metered connections
}

// Default polling configuration
export const DEFAULT_POLLING_CONFIG: PollingConfiguration = {
  userHazardsInterval: 30000, // 30 seconds
  communityHazardsInterval: 60000, // 1 minute  
  enabled: true,
  maxRetries: 3,
  retryBackoffMs: 5000, // 5 seconds
  enabledOnMobile: true,
  enabledOnMeteredConnection: false // Conservative default
};

// Preset configurations for different scenarios
export const POLLING_PRESETS = {
  // Aggressive polling for high-activity environments
  HIGH_FREQUENCY: {
    ...DEFAULT_POLLING_CONFIG,
    userHazardsInterval: 15000, // 15 seconds
    communityHazardsInterval: 30000, // 30 seconds
  } as PollingConfiguration,

  // Normal polling (default)
  NORMAL: DEFAULT_POLLING_CONFIG,

  // Conservative polling to save bandwidth and battery
  LOW_FREQUENCY: {
    ...DEFAULT_POLLING_CONFIG,
    userHazardsInterval: 60000, // 1 minute
    communityHazardsInterval: 180000, // 3 minutes
    enabledOnMeteredConnection: false
  } as PollingConfiguration,

  // Battery saver mode
  BATTERY_SAVER: {
    ...DEFAULT_POLLING_CONFIG,
    userHazardsInterval: 120000, // 2 minutes
    communityHazardsInterval: 300000, // 5 minutes
    enabledOnMobile: false,
    enabledOnMeteredConnection: false
  } as PollingConfiguration
};

/**
 * Utility class for managing polling configuration
 */
export class PollingConfigManager {
  private static readonly STORAGE_KEY = 'safework_polling_config';

  /**
   * Load configuration from localStorage with fallback to defaults
   */
  static loadConfig(): PollingConfiguration {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Merge with defaults to ensure all properties are present
        return { ...DEFAULT_POLLING_CONFIG, ...parsed };
      }
    } catch (error) {
      console.warn('Error loading polling config from localStorage:', error);
    }
    
    return { ...DEFAULT_POLLING_CONFIG };
  }

  /**
   * Save configuration to localStorage
   */
  static saveConfig(config: PollingConfiguration): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(config));
    } catch (error) {
      console.error('Error saving polling config to localStorage:', error);
    }
  }

  /**
   * Reset to default configuration
   */
  static resetConfig(): PollingConfiguration {
    const config = { ...DEFAULT_POLLING_CONFIG };
    this.saveConfig(config);
    return config;
  }

  /**
   * Apply a preset configuration
   */
  static applyPreset(preset: keyof typeof POLLING_PRESETS): PollingConfiguration {
    const config = { ...POLLING_PRESETS[preset] };
    this.saveConfig(config);
    return config;
  }

  /**
   * Get configuration adjusted for current connection state
   */
  static getAdaptiveConfig(baseConfig?: PollingConfiguration): PollingConfiguration {
    const config = baseConfig || this.loadConfig();
    
    // Check if we should disable polling based on connection state
    if (!navigator.onLine) {
      return { ...config, enabled: false };
    }

    // Check for metered connection (when supported)
    if ('connection' in navigator) {
      const connection = (navigator as any).connection;
      if (connection && connection.saveData && !config.enabledOnMeteredConnection) {
        return { ...config, enabled: false };
      }
    }

    // Check if we're on mobile and mobile polling is disabled
    if (!config.enabledOnMobile && this.isMobileDevice()) {
      return { ...config, enabled: false };
    }

    return config;
  }

  /**
   * Simple mobile device detection
   */
  private static isMobileDevice(): boolean {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  /**
   * Get recommended configuration based on device and connection
   */
  static getRecommendedConfig(): PollingConfiguration {
    // On mobile or slow connections, use conservative settings
    if (this.isMobileDevice()) {
      return { ...POLLING_PRESETS.LOW_FREQUENCY };
    }

    // Check connection speed if available
    if ('connection' in navigator) {
      const connection = (navigator as any).connection;
      if (connection && connection.effectiveType) {
        // Slow connections get conservative polling
        if (connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g') {
          return { ...POLLING_PRESETS.BATTERY_SAVER };
        }
        // Fast connections can handle higher frequency
        if (connection.effectiveType === '4g') {
          return { ...POLLING_PRESETS.HIGH_FREQUENCY };
        }
      }
    }

    // Default to normal frequency
    return { ...DEFAULT_POLLING_CONFIG };
  }
}

/**
 * Hook-like utility for components that need polling config
 */
export const getPollingConfig = () => {
  return PollingConfigManager.getAdaptiveConfig();
};